import {
  createImportRemoteListener,
  report,
  REPORT_TYPES,
} from '@adsk/exo-skeleton-api';
import {
  setExoSkeletonRemotes,
  setIsInExoSkeleton,
  type RuntimeRemoteInfo,
  getExoSkeletonRuntime,
} from '@adsk/exo-skeleton-api/es/internal';

import { getRemoteOverride } from './getRemoteOverrides';
import {
  EXO_SKELETON_SCOPE_NAME,
  IMPORT_REMOTE_RUNTIME_EVENT_NAME,
} from '../consts';
import { getEnvConfig } from '../envs/envConfig';

export type ExtendedRuntimeRemoteInfo = RuntimeRemoteInfo & {
  override: boolean;
  moniker: string | undefined;
};

function createRuntimeRemotes(): Record<string, RuntimeRemoteInfo> {
  const { remotes } = getEnvConfig();
  const runtimeRemotes: Record<string, ExtendedRuntimeRemoteInfo> = {};

  for (const { scope, url, moniker } of remotes) {
    const overrideUrl = getRemoteOverride(scope);
    runtimeRemotes[scope] = {
      override: Boolean(overrideUrl),
      moniker,
      get() {
        return Promise.resolve({
          url: overrideUrl || url,
          scope,
        });
      },
    };
  }

  return runtimeRemotes;
}

export function getRemoteRuntime(
  scope: string,
): ExtendedRuntimeRemoteInfo | undefined {
  const runtime = getExoSkeletonRuntime();
  return runtime?.remotes?.[scope] as ExtendedRuntimeRemoteInfo | undefined;
}

export function initializeExoSkeletonRuntimeValues() {
  setIsInExoSkeleton();
  setExoSkeletonRemotes(createRuntimeRemotes());

  createImportRemoteListener({
    onImportRemote({ data: { scope, metadata } }) {
      const { moniker, override } = getRemoteRuntime(scope) || {};

      report({
        type: REPORT_TYPES.info,
        value: IMPORT_REMOTE_RUNTIME_EVENT_NAME,
        scope: EXO_SKELETON_SCOPE_NAME,
        context: {
          moniker,
          override,
          scope,
          metadata,
        },
      });
    },
  });
}

export function ensureRemote(scope: string): boolean {
  const { remotes } = getEnvConfig();

  if (!remotes) {
    throw new Error('ExoSkeleton remotes are not available');
  }

  return Boolean(remotes.find((remote) => remote.scope === scope));
}
